import React from "react";
import useLang from "../../../../hooks/useLang";

const TranscriptionView = ({ summary, onSummaryTranscriptionClick }) => {
  const [lang] = useLang();

  // Function to convert capital case text to title case
  const formatCapitalText = text => {
    if (!text) return "";
    return text
      .toLowerCase()
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Function to parse and format the summary content
  const parseSummaryContent = htmlContent => {
    try {
      // Create a temporary div to parse HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;

      const lines = htmlContent
        .split("<br/>")
        .map(line => line.trim())
        .filter(line => line);
      const parsedData = {};
      let aiSummaryContent = "";

      let isAISummary = false;

      lines.forEach(line => {
        // Remove HTML tags for processing
        const cleanLine = line.replace(/<[^>]*>/g, "").trim();

        if (cleanLine.startsWith("AI Summary:")) {
          isAISummary = true;
          aiSummaryContent = cleanLine.replace("AI Summary:", "").trim();
          return;
        }

        if (isAISummary) {
          aiSummaryContent += ` ${cleanLine}`;
          return;
        }

        // Skip certain lines
        if (cleanLine.includes("This activity is generated by EngagedAI")) {
          return;
        }

        // Handle Listen to Call links specially
        if (cleanLine.includes("Listen to Call:")) {
          const linkMatch = line.match(/Listen to Call:\s*<a[^>]*>(.*?)<\/a>/);
          if (linkMatch) {
            parsedData["Listen to Call"] = line; // Keep the full HTML for the link
          }
          return;
        }

        // Handle Summary and Transcription (this will be added programmatically)
        if (cleanLine.includes("Summary and Transcription:")) {
          parsedData["Summary and Transcription"] = line;
          return;
        }

        // Parse key-value pairs
        const colonIndex = cleanLine.indexOf(":");
        if (colonIndex > 0) {
          const key = cleanLine.substring(0, colonIndex).trim();
          const value = cleanLine.substring(colonIndex + 1).trim();

          if (key && value) {
            parsedData[key] = value;
          }
        }
      });

      if (aiSummaryContent) {
        parsedData["AI Summary"] = aiSummaryContent;
      }

      // Reorder to ensure Summary and Transcription comes after Listen to Call but before AI Summary
      const orderedData = {};
      const keyOrder = [
        "DIRECTION",
        "Duration",
        "Status",
        "Listen to Call",
        "Summary and Transcription",
        "AI Summary",
      ];

      // Add Summary and Transcription if Listen to Call exists, but before processing keyOrder
      if (parsedData["Listen to Call"]) {
        parsedData["Summary and Transcription"] = "Click here";
      }

      keyOrder.forEach(key => {
        if (parsedData[key]) {
          orderedData[key] = parsedData[key];
        }
      });

      // Add any remaining keys not in the order
      Object.keys(parsedData).forEach(key => {
        if (!orderedData[key]) {
          orderedData[key] = parsedData[key];
        }
      });

      return orderedData;
    } catch (error) {
      console.error("Error parsing summary content:", error);
      return null;
    }
  };

  // Function to format field labels
  const formatFieldLabel = key => {
    const labelMappings = {
      DIRECTION: "Direction",
      Duration: "Duration",
      Status: "Status",
      "Listen to Call": "Listen to Call",
      "Summary and Transcription": "Summary and Transcription",
      "AI Summary": "AI Summary",
    };

    return labelMappings[key] || key;
  };

  // Function to format field values
  const formatFieldValue = (key, value) => {
    // Handle Listen to Call links - return raw HTML
    if (key === "Listen to Call") {
      return (
        <span dangerouslySetInnerHTML={{ __html: value.replace("Listen to Call:", "").trim() }} />
      );
    }

    // Handle Summary and Transcription - return clickable link
    if (key === "Summary and Transcription") {
      return (
        <span
          className="text-blue-600 cursor-pointer hover:text-blue-800"
          onClick={() => onSummaryTranscriptionClick && onSummaryTranscriptionClick()}
        >
          Click here
        </span>
      );
    }

    const valueMappings = {
      INBOUND: "Inbound",
      OUTBOUND: "Outbound",
      LEFT_DIAL_PLAN: "Left Dial Plan",
      NORMAL: "Normal",
    };

    // Check if it's a capital case value that needs formatting
    if (valueMappings[value]) {
      return valueMappings[value];
    }

    // For other capital case values, use the formatCapitalText function
    if (/^[A-Z_]+$/.test(value)) {
      return formatCapitalText(value);
    }

    return value;
  };

  // Safe HTML rendering function
  const renderSummaryContent = () => {
    try {
      // Check if summary exists and is not empty
      if (!summary || summary.trim() === "") {
        return <span className="text-grey-500 italic">{lang.transcriptions.noSummary}</span>;
      }

      // Check if the content appears to be HTML (contains HTML tags)
      const containsHTML = /<[^>]*>/.test(summary);

      if (containsHTML) {
        // Parse the HTML content into structured data
        const parsedData = parseSummaryContent(summary);

        if (parsedData && Object.keys(parsedData).length > 0) {
          return (
            <div>
              {Object.entries(parsedData).map(([key, value]) => (
                <div key={key} className={key === "AI Summary" ? "mt-4" : ""}>
                  {key === "AI Summary" ? (
                    // AI Summary gets its own line structure
                    <>
                      <div className="font-semibold text-grey-800">{formatFieldLabel(key)}:</div>
                      <div className="text-grey-700 mt-1">{formatFieldValue(key, value)}</div>
                    </>
                  ) : (
                    // Other fields are inline
                    <div className="text-grey-700">
                      <span className="font-semibold text-grey-800">{formatFieldLabel(key)}:</span>{" "}
                      {formatFieldValue(key, value)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        }

        // Fallback to original HTML rendering if parsing fails
        return <div className="text-grey-700" dangerouslySetInnerHTML={{ __html: summary }} />;
      }

      // Render as plain text but preserve line breaks
      return <span className="text-grey-700 whitespace-pre-wrap">{summary}</span>;
    } catch (error) {
      console.error("Error rendering summary content:", error);
      return <span className="text-red-500 italic">{lang.transcriptions.errorLoadingSummary}</span>;
    }
  };

  return (
    <div className="p-4 bg-[#CDDAE9] rounded-lg rounded-tr-none gap-2">
      <div className="flex items-center gap-2 justify-start mb-2">
        <span className="sm-600 text-grey-800">{lang.transcriptions.callSummary}</span>
        <span className="xs-400 text-grey-500">{lang.transcriptions.poweredByEngageAi}</span>
      </div>
      {renderSummaryContent()}
    </div>
  );
};

export default TranscriptionView;
